import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPoojaris, approvePoojari, rejectPoojari, suspendPoojari } from '../../services/adminService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { formatPaise } from '../../utils/money';

const ManagePoojaris = () => {
    const [poojaris, setPoojaris] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('ALL');
    const { token } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/admin-login');
            return;
        }
        fetchPoojaris();
    }, []);

    const fetchPoojaris = async () => {
        try {
            const data = await getAllPoojaris();
            setPoojaris(data);
        } catch (error) {
            toast.error('Error fetching poojaris');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        try {
            await approvePoojari(id);
            toast.success('Poojari approved successfully!');
            fetchPoojaris();
        } catch (error) {
            toast.error('Error approving poojari');
        }
    };

    const handleReject = async (id) => {
        try {
            await rejectPoojari(id);
            toast.success('Poojari rejected!');
            fetchPoojaris();
        } catch (error) {
            toast.error('Error rejecting poojari');
        }
    };

    const handleSuspend = async (id) => {
        try {
            await suspendPoojari(id);
            toast.success('Poojari suspended!');
            fetchPoojaris();
        } catch (error) {
            toast.error('Error suspending poojari');
        }
    };

    const filteredPoojaris = filter === 'ALL'
        ? poojaris
        : poojaris.filter(p => p.status === filter);

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: '#8B4513' }}>
            </div>
        </div>
    );

    return (
        <div className="container py-4">
            <h2 className="fw-bold mb-4">🙏 Manage Poojaris</h2>

            {/* Filter */}
            <div className="btn-group mb-4">
                {['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED'].map(status => (
                    <button
                        key={status}
                        className={`btn ${filter === status
                            ? 'btn-warning'
                            : 'btn-outline-warning'}`}
                        onClick={() => setFilter(status)}>
                        {status}
                        {status === 'PENDING' && (
                            <span className="badge bg-danger ms-1">
                                {poojaris.filter(p =>
                                    p.status === 'PENDING').length}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {filteredPoojaris.length === 0 ? (
                <p className="text-muted text-center py-4">
                    No poojaris found
                </p>
            ) : (
                <div className="row g-3">
                    {filteredPoojaris.map(poojari => (
                        <div key={poojari.id} className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-md-8">
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <h5 className="mb-0">
                                                    {poojari.firstName} {poojari.lastName}
                                                </h5>
                                                <span className={`badge bg-${
                                                    poojari.status === 'APPROVED'
                                                    ? 'success'
                                                    : poojari.status === 'PENDING'
                                                    ? 'warning'
                                                    : poojari.status === 'REJECTED'
                                                    ? 'danger'
                                                    : 'dark'}`}>
                                                    {poojari.status}
                                                </span>
                                            </div>
                                            <p className="mb-1">
                                                📧 {poojari.email}
                                            </p>
                                            <p className="mb-1">
                                                📞 {poojari.phoneNumber}
                                            </p>
                                            <p className="mb-1">
                                                📍 {poojari.city}, {poojari.state}
                                            </p>
                                            <p className="mb-1">
                                                🎯 {poojari.experienceYears} years exp |
                                                {formatPaise(poojari.chargePerPooja)} per pooja
                                            </p>
                                            <div>
                                                {poojari.specializations
                                                    ?.slice(0, 3)
                                                    .map((spec, i) => (
                                                    <span key={i}
                                                        className="badge me-1"
                                                        style={{
                                                            backgroundColor: '#8B4513'
                                                        }}>
                                                        {spec}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="d-flex flex-column gap-2">
                                                {poojari.status === 'PENDING' && (
                                                    <>
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={() =>
                                                                handleApprove(poojari.id)}>
                                                            ✅ Approve
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() =>
                                                                handleReject(poojari.id)}>
                                                            ❌ Reject
                                                        </button>
                                                    </>
                                                )}
                                                {poojari.status === 'APPROVED' && (
                                                    <button
                                                        className="btn btn-warning btn-sm"
                                                        onClick={() =>
                                                            handleSuspend(poojari.id)}>
                                                        ⚠️ Suspend
                                                    </button>
                                                )}
                                                {poojari.status === 'SUSPENDED' && (
                                                    <button
                                                        className="btn btn-success btn-sm"
                                                        onClick={() =>
                                                            handleApprove(poojari.id)}>
                                                        ✅ Re-Approve
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ManagePoojaris;